import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { MiniQuizProvider } from './contexts/MiniQuizContext';
import MobileLayout from './components/MobileLayout';
import HomePage from './pages/HomePage';
import GamePage from './pages/GamePage';
import ComponentDemoPage from './pages/ComponentDemoPage';
import DailyCheckinPage from './pages/DailyCheckinPage';
import KilobaHubPage from './pages/KilobaHubPage';
import KilobaCollection from './pages/KilobaCollection';
import MobileLayoutDemo from './pages/MobileLayoutDemo';
import MiniGamePage from './pages/MiniGamePage';
import MiniQuizPage from './pages/MiniQuizPage';
import MiniQuizTestPage from './pages/MiniQuizTestPage';
import MiniQuizPageWithContext from './pages/MiniQuizPageWithContext';
import LoginPage from './pages/LoginPage';
import TaskPage from './pages/TaskPage';
import CloudTowerGamePage from './pages/CloudTowerGamePage';
import KilobaRacingPage from './pages/KilobaRacingPage';
import GameTestPage from './pages/GameTestPage';
import NotificationPage from './pages/NotificationPage';
import NotificationTestPage from './pages/NotificationTestPage';
import NotificationFallbackTestPage from './pages/NotificationFallbackTestPage';
import SettingsPage from './pages/SettingsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import EventJoinPage from './pages/EventJoinPage';
import EventQRScanPage from './pages/EventQRScanPage';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import InstallPWAButton from './components/InstallPWAButton';
import TestLogoutButton from './components/TestLogoutButton';
import TestTermsButton from './components/TestTermsButton';
import NotificationBadge, { NotificationData } from './components/NotificationBadge';
import { usePWA } from './hooks/usePWA';
import { useNotifications } from './hooks/useNotifications';
import { useState, useEffect } from 'react';
import NotificationFallback from './utils/notificationFallback';
import './index.css';

function App() {
  const {
    isInstallable,
    isInstalled,
    needRefresh,
    offlineReady,
    installPWA,
    updatePWA,
    closeUpdatePrompt,
    closeOfflineNotification,
  } = usePWA();

  const { requestPermission, isSupported, error } = useNotifications();
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const [showNotificationWarning, setShowNotificationWarning] = useState(false);
  const [dismissedError, setDismissedError] = useState<string | null>(null);

  // Request notification permission on app load (optional, can be triggered by user action)
  useEffect(() => {
    // Kiểm tra xem trình duyệt có hỗ trợ notifications không
    if (!isSupported) {
      console.warn('Notifications are not supported in this browser');
      setShowNotificationWarning(true);
      
      // Hiển thị cảnh báo bằng fallback system
      NotificationFallback.showUnsupportedWarning();
      
      // Tự động ẩn cảnh báo sau 10 giây
      const warningTimer = setTimeout(() => {
        setShowNotificationWarning(false);
      }, 10000);
      
      return () => clearTimeout(warningTimer);
    }

    // Kiểm tra xem API Notification có tồn tại không
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('Notification API is not available in this environment');
      return;
    }
  }, [requestPermission, isSupported]);

  // Handle notification from service worker or foreground
  useEffect(() => {
    const handleNotificationReceived = (event: CustomEvent<NotificationData>) => {
      setCurrentNotification(event.detail);
    };

    // Listen for custom notification events
    window.addEventListener('notification-received' as any, handleNotificationReceived);

    return () => {
      window.removeEventListener('notification-received' as any, handleNotificationReceived);
    };
  }, []);

  const handleNotificationAction = (action: string) => {
    
    if (action === 'open' && currentNotification?.data?.click_action) {
      window.location.href = currentNotification.data.click_action;
    }
    
    setCurrentNotification(null);
  };

  const handleNotificationClose = () => {
    setCurrentNotification(null);
  };

  return (
    <MobileLayout>
            <AuthProvider>
        <MiniQuizProvider>
          <Router>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/demo" element={<ComponentDemoPage />} />
              <Route path="/mobile-demo" element={<MobileLayoutDemo />} />
              <Route path="/daily-checkin" element={<DailyCheckinPage />} />
              <Route path="/kiloba-hub" element={<KilobaHubPage />} />
              <Route path="/kiloba-collection" element={<KilobaCollection />} />
              <Route path="/game" element={<GamePage />} />
              <Route path="/mini-game" element={<MiniGamePage />} />
              <Route path="/cloud-tower-game" element={<CloudTowerGamePage />} />
              <Route path="/kiloba-racing" element={<KilobaRacingPage />} />
              <Route path="/mini-quiz" element={<MiniQuizPageWithContext />} />
              <Route path="/mini-quiz-test" element={<MiniQuizTestPage />} />
              <Route path="/game-test" element={<GameTestPage />} />
              <Route path="/tasks" element={<TaskPage />} />
              <Route path="/notifications" element={<NotificationPage />} />
              <Route path="/notification-test" element={<NotificationTestPage />} />
              <Route path="/notification-fallback-test" element={<NotificationFallbackTestPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/event-join" element={<EventJoinPage />} />
              <Route path="/event-qr-scan" element={<EventQRScanPage />} />
              <Route path="/join-event/:token" element={<EventQRScanPage />} />
            </Routes>
          </Router>

          
        </MiniQuizProvider>
      </AuthProvider>

      {/* PWA Components */}
      <PWAUpdatePrompt
        show={needRefresh}
        onUpdate={updatePWA}
        onClose={closeUpdatePrompt}
      />
      
      <InstallPWAButton
        isInstallable={isInstallable}
        isInstalled={isInstalled}
        onInstall={installPWA}
      />

      {/* Firebase Notification Badge */}
      <NotificationBadge
        notification={currentNotification}
        onClose={handleNotificationClose}
        onAction={handleNotificationAction}
        autoHideDelay={6000}
      />

      {/* Offline Ready Notification */}
      

      {/* Notification Error Warning */}
      {error && error !== dismissedError && (
        <div 
          style={{
            position: 'fixed',
            bottom: showNotificationWarning ? '170px' : '120px',
            left: '20px',
            background: '#f44336',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            zIndex: 1000,
            maxWidth: '280px',
            lineHeight: '1.4',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          onClick={() => {
            setDismissedError(error);
          }}
        >
          <span>🚫 Lỗi thông báo: {error}</span>
          <span style={{ marginLeft: '8px', fontSize: '16px' }}>×</span>
        </div>
      )}
    </MobileLayout>
  );
}

export default App;
